import * as Yup from 'yup';
import {LectureFormValues} from './types';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';

/**
 * The lecture `link` field is stored as a JSON string: {title, link}.
 * Older records may hold a plain URL string, so we fall back gracefully.
 */
export const parseLectureLink = (raw?: string | null): {linkName: string; link: string} => {
    if(!raw) return {linkName: '', link: ''};
    try {
        const parsed = JSON.parse(raw);
        if(parsed && typeof parsed === 'object')
            return {linkName: parsed.title ?? '', link: parsed.link ?? ''};
    } catch {
        // legacy plain URL
    }
    return {linkName: '', link: raw};
};

export const serializeLectureLink = (linkName: string, link: string): string | null => {
    const title = (linkName ?? '').trim();
    const url = (link ?? '').trim();
    if(!title && !url) return null;
    return JSON.stringify({title, link: url});
};

export const initialValueLecture = (lecture?: TTimetableItem | null): Omit<LectureFormValues, 'dayIndex'> => ({
    title: lecture?.title ?? '',
    lecturerIds: lecture?.lecturers?.map(l => l.lid) ?? [],
    tids: lecture?.tickets?.map(t => t.tid) ?? [],
    thid: lecture?.thid ?? null,
    tsid: lecture?.tsid ?? null,
    description: lecture?.description ?? '',
    amount: lecture ? lecture.amount ?? null : 0,
    price: lecture?.price ?? 0,
    hasCasualPrice: lecture?.priceCasual != null,
    priceCasual: lecture?.priceCasual ?? 0,
    isRequired: !!lecture?.isRequired,
    ...parseLectureLink(lecture?.link),
    active: lecture? !!lecture?.active : true,
});

export const buildValidationSchema = (
    requiredMessage: string,
    linkInvalidMessage: string,
    linkTooLongMessage: string,
    linkNameTooLongMessage: string,
    integerMessage: string,
) =>
    Yup.object({
        title: Yup.string().required(requiredMessage),
        tsid: Yup.number().nullable().required(requiredMessage),
        dayIndex: Yup.number().min(-1, requiredMessage).required(requiredMessage),
        // Unlimited capacity is stored as null (the field is disabled then); any entered
        // value must be 1+. `defined()` rejects undefined (a cleared field), while the
        // empty string and an explicit 0 are caught by typeError/min — so all three are blocked.
        amount: Yup.number().nullable().defined(requiredMessage).min(1, requiredMessage).typeError(requiredMessage).integer(integerMessage),
        price: Yup.number().min(0).integer(integerMessage),
        priceCasual: Yup.number().min(0).integer(integerMessage),
        link: Yup.string().url(linkInvalidMessage).max(1000, linkTooLongMessage),
        linkName: Yup.string().max(20, linkNameTooLongMessage),
    });
